import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { PageContainer, PageLoader } from '@mpange/ui';
import { AdminLayout } from '@/layouts/AdminLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { DocsLayout } from '@/layouts/DocsLayout';
import { MarketingLayout } from '@/layouts/MarketingLayout';
import { OnboardingGuard } from '@/components/onboarding-guard';

function LegacyDocsRedirect() {
  const { slug } = useParams();
  if (!slug) return <Navigate to="/docs" replace />;
  return <Navigate to={{ pathname: '/docs', hash: `#${slug}` }} replace />;
}

function PageFallback() {
  return (
    <PageContainer>
      <PageLoader />
    </PageContainer>
  );
}

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageFallback />}>{children}</Suspense>;
}

const MarketingHomePage = lazy(() => import('@/app/(marketing)/page'));
const AboutPage = lazy(() => import('@/app/(marketing)/about/page'));
const ContactPage = lazy(() => import('@/app/(marketing)/contact/page'));
const DevelopersMarketingPage = lazy(() => import('@/app/(marketing)/developers/page'));
const PartnersPage = lazy(() => import('@/app/(marketing)/partners/page'));
const PricingPage = lazy(() => import('@/app/(marketing)/pricing/page'));
const SolutionsPage = lazy(() => import('@/app/(marketing)/solutions/page'));
const ProductSmsPage = lazy(() => import('@/app/(marketing)/products/sms/page'));
const ProductUssdPage = lazy(() => import('@/app/(marketing)/products/ussd/page'));
const ProductVoicePage = lazy(() => import('@/app/(marketing)/products/voice/page'));
const ProductIvrPage = lazy(() => import('@/app/(marketing)/products/ivr/page'));
const ProductWhatsappPage = lazy(() => import('@/app/(marketing)/products/whatsapp/page'));
const ProductApisPage = lazy(() => import('@/app/(marketing)/products/apis/page'));
const DocsOverviewPage = lazy(() => import('@/app/(docs)/overview/page'));
const DocsTopicRoutePage = lazy(() => import('@/app/(docs)/topic/page'));

const LoginPage = lazy(() => import('@/app/(auth)/login/page'));
const RegisterPage = lazy(() => import('@/app/(auth)/register/page'));
const ForgotPasswordPage = lazy(() => import('@/app/(auth)/forgot-password/page'));
const ResetPasswordPage = lazy(() => import('@/app/(auth)/reset-password/page'));
const VerifyEmailPage = lazy(() => import('@/app/(auth)/verify-email/page'));
const VerifyEmailPendingPage = lazy(() => import('@/app/(auth)/verify-email/pending/page'));

const OnboardingPage = lazy(() => import('@/app/onboarding/page'));

const DashboardPage = lazy(() => import('@/app/dashboard/page'));
const DashboardAnalyticsPage = lazy(() => import('@/app/dashboard/analytics/page'));
const DashboardAuditPage = lazy(() => import('@/app/dashboard/audit/page'));
const DashboardBillingPage = lazy(() => import('@/app/dashboard/billing/page'));
const DashboardBillingTopupPage = lazy(() => import('@/app/dashboard/billing/topup/page'));
const DashboardBillingInvoicePage = lazy(() => import('@/app/dashboard/billing/invoices/[invoiceId]/page'));
const DashboardCampaignsPage = lazy(() => import('@/app/dashboard/campaigns/page'));
const DashboardCampaignsNewPage = lazy(() => import('@/app/dashboard/campaigns/new/page'));
const DashboardCampaignDetailPage = lazy(() => import('@/app/dashboard/campaigns/[campaignId]/page'));
const DashboardChannelsPage = lazy(() => import('@/app/dashboard/channels/page'));
const DashboardIvrPage = lazy(() => import('@/app/dashboard/ivr/page'));
const DashboardIvrFlowPage = lazy(() => import('@/app/dashboard/ivr/flows/[flowId]/page'));
const DashboardIvrExecutionsPage = lazy(() => import('@/app/dashboard/ivr/executions/page'));
const DashboardIvrExecutionDetailPage = lazy(() => import('@/app/dashboard/ivr/executions/[executionId]/page'));
const DashboardContactsPage = lazy(() => import('@/app/dashboard/contacts/page'));
const DashboardContactDetailPage = lazy(() => import('@/app/dashboard/contacts/[contactId]/page'));
const DashboardDevelopersPage = lazy(() => import('@/app/dashboard/developers/page'));
const DashboardDevelopersKeysPage = lazy(() => import('@/app/dashboard/developers/keys/page'));
const DashboardKycPage = lazy(() => import('@/app/dashboard/kyc/page'));
const DashboardOrganizationPage = lazy(() => import('@/app/dashboard/organization/page'));
const DashboardResellerPage = lazy(() => import('@/app/dashboard/reseller/page'));
const DashboardSenderIdsPage = lazy(() => import('@/app/dashboard/sender-ids/page'));
const DashboardSmsPage = lazy(() => import('@/app/dashboard/sms/page'));
const DashboardSmsSendPage = lazy(() => import('@/app/dashboard/sms/send/page'));
const DashboardSmsMessagePage = lazy(() => import('@/app/dashboard/sms/[messageId]/page'));
const DashboardSupportPage = lazy(() => import('@/app/dashboard/support/page'));
const DashboardSupportTicketPage = lazy(() => import('@/app/dashboard/support/[ticketId]/page'));
const DashboardTeamPage = lazy(() => import('@/app/dashboard/team/page'));

const AdminHomePage = lazy(() => import('@/app/admin/page'));
const AdminAuditPage = lazy(() => import('@/app/admin/audit/page'));
const AdminContractsPage = lazy(() => import('@/app/admin/contracts/page'));
const AdminKycPage = lazy(() => import('@/app/admin/kyc/page'));
const AdminOrganizationsPage = lazy(() => import('@/app/admin/organizations/page'));
const AdminPaymentsPage = lazy(() => import('@/app/admin/payments/page'));
const AdminProvidersPage = lazy(() => import('@/app/admin/providers/page'));
const AdminReconciliationPage = lazy(() => import('@/app/admin/reconciliation/page'));
const AdminSenderIdsPage = lazy(() => import('@/app/admin/sender-ids/page'));
const AdminSupportPage = lazy(() => import('@/app/admin/support/page'));
const AdminUsersPage = lazy(() => import('@/app/admin/users/page'));

export function AppRoutes() {
  return (
    <Routes>
      <Route element={<MarketingLayout />}>
        <Route index element={<Lazy><MarketingHomePage /></Lazy>} />
        <Route path="about" element={<Lazy><AboutPage /></Lazy>} />
        <Route path="contact" element={<Lazy><ContactPage /></Lazy>} />
        <Route path="developers" element={<Lazy><DevelopersMarketingPage /></Lazy>} />
        <Route path="partners" element={<Lazy><PartnersPage /></Lazy>} />
        <Route path="pricing" element={<Lazy><PricingPage /></Lazy>} />
        <Route path="solutions" element={<Lazy><SolutionsPage /></Lazy>} />
        <Route path="products/sms" element={<Lazy><ProductSmsPage /></Lazy>} />
        <Route path="products/ussd" element={<Lazy><ProductUssdPage /></Lazy>} />
        <Route path="products/voice" element={<Lazy><ProductVoicePage /></Lazy>} />
        <Route path="products/ivr" element={<Lazy><ProductIvrPage /></Lazy>} />
        <Route path="products/whatsapp" element={<Lazy><ProductWhatsappPage /></Lazy>} />
        <Route path="products/apis" element={<Lazy><ProductApisPage /></Lazy>} />
      </Route>

      <Route path="docs" element={<DocsLayout />}>
        <Route index element={<Lazy><DocsOverviewPage /></Lazy>} />
        <Route path=":slug" element={<Lazy><DocsTopicRoutePage /></Lazy>} />
      </Route>
      <Route path="docs/2026-01-25" element={<Navigate to="/docs" replace />} />
      <Route path="docs/2026-01-25/:slug" element={<LegacyDocsRedirect />} />

      <Route element={<AuthLayout />}>
        <Route path="login" element={<Lazy><LoginPage /></Lazy>} />
        <Route path="register" element={<Lazy><RegisterPage /></Lazy>} />
        <Route path="forgot-password" element={<Lazy><ForgotPasswordPage /></Lazy>} />
        <Route path="reset-password" element={<Lazy><ResetPasswordPage /></Lazy>} />
        <Route path="verify-email" element={<Lazy><VerifyEmailPage /></Lazy>} />
        <Route path="verify-email/pending" element={<Lazy><VerifyEmailPendingPage /></Lazy>} />
      </Route>

      <Route
        path="onboarding"
        element={
          <OnboardingGuard>
            <Lazy>
              <OnboardingPage />
            </Lazy>
          </OnboardingGuard>
        }
      />

      <Route element={<DashboardLayout />}>
        <Route path="dashboard" element={<Lazy><DashboardPage /></Lazy>} />
        <Route path="dashboard/analytics" element={<Lazy><DashboardAnalyticsPage /></Lazy>} />
        <Route path="dashboard/audit" element={<Lazy><DashboardAuditPage /></Lazy>} />
        <Route path="dashboard/billing" element={<Lazy><DashboardBillingPage /></Lazy>} />
        <Route path="dashboard/billing/topup" element={<Lazy><DashboardBillingTopupPage /></Lazy>} />
        <Route path="dashboard/billing/invoices/:invoiceId" element={<Lazy><DashboardBillingInvoicePage /></Lazy>} />
        <Route path="dashboard/campaigns" element={<Lazy><DashboardCampaignsPage /></Lazy>} />
        <Route path="dashboard/campaigns/new" element={<Lazy><DashboardCampaignsNewPage /></Lazy>} />
        <Route path="dashboard/campaigns/:campaignId" element={<Lazy><DashboardCampaignDetailPage /></Lazy>} />
        <Route path="dashboard/channels" element={<Lazy><DashboardChannelsPage /></Lazy>} />
        <Route path="dashboard/ivr" element={<Lazy><DashboardIvrPage /></Lazy>} />
        <Route path="dashboard/ivr/executions" element={<Lazy><DashboardIvrExecutionsPage /></Lazy>} />
        <Route path="dashboard/ivr/executions/:executionId" element={<Lazy><DashboardIvrExecutionDetailPage /></Lazy>} />
        <Route path="dashboard/ivr/flows/:flowId" element={<Lazy><DashboardIvrFlowPage /></Lazy>} />
        <Route path="dashboard/contacts" element={<Lazy><DashboardContactsPage /></Lazy>} />
        <Route path="dashboard/contacts/:contactId" element={<Lazy><DashboardContactDetailPage /></Lazy>} />
        <Route path="dashboard/developers" element={<Lazy><DashboardDevelopersPage /></Lazy>} />
        <Route path="dashboard/developers/keys" element={<Lazy><DashboardDevelopersKeysPage /></Lazy>} />
        <Route path="dashboard/kyc" element={<Lazy><DashboardKycPage /></Lazy>} />
        <Route path="dashboard/organization" element={<Lazy><DashboardOrganizationPage /></Lazy>} />
        <Route path="dashboard/reseller" element={<Lazy><DashboardResellerPage /></Lazy>} />
        <Route path="dashboard/sender-ids" element={<Lazy><DashboardSenderIdsPage /></Lazy>} />
        <Route path="dashboard/sms" element={<Lazy><DashboardSmsPage /></Lazy>} />
        <Route path="dashboard/sms/send" element={<Lazy><DashboardSmsSendPage /></Lazy>} />
        <Route path="dashboard/sms/:messageId" element={<Lazy><DashboardSmsMessagePage /></Lazy>} />
        <Route path="dashboard/support" element={<Lazy><DashboardSupportPage /></Lazy>} />
        <Route path="dashboard/support/:ticketId" element={<Lazy><DashboardSupportTicketPage /></Lazy>} />
        <Route path="dashboard/team" element={<Lazy><DashboardTeamPage /></Lazy>} />
      </Route>

      <Route element={<AdminLayout />}>
        <Route path="admin" element={<Lazy><AdminHomePage /></Lazy>} />
        <Route path="admin/audit" element={<Lazy><AdminAuditPage /></Lazy>} />
        <Route path="admin/contracts" element={<Lazy><AdminContractsPage /></Lazy>} />
        <Route path="admin/kyc" element={<Lazy><AdminKycPage /></Lazy>} />
        <Route path="admin/organizations" element={<Lazy><AdminOrganizationsPage /></Lazy>} />
        <Route path="admin/payments" element={<Lazy><AdminPaymentsPage /></Lazy>} />
        <Route path="admin/providers" element={<Lazy><AdminProvidersPage /></Lazy>} />
        <Route path="admin/reconciliation" element={<Lazy><AdminReconciliationPage /></Lazy>} />
        <Route path="admin/sender-ids" element={<Lazy><AdminSenderIdsPage /></Lazy>} />
        <Route path="admin/support" element={<Lazy><AdminSupportPage /></Lazy>} />
        <Route path="admin/users" element={<Lazy><AdminUsersPage /></Lazy>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
